import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router';
import { Main, Login } from 'screens';
import ProtectedRoute from './ProtectedRoute';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import AuthRoute from './AuthRoute';
import { trackPageView } from 'utils/analytics';
import MyCards from 'screens/Main/tabs/MyCards';
import HomePage from 'screens/Main/tabs/Home/HomePage';
import TermsOfService from 'screens/Legal/TermsOfService';
import PrivacyPolicy from 'screens/Legal/PrivacyPolicy';
import OnboardingFlow from '../screens/Auth/Login/Onboarding/OnboardingFlow';
import ConditionGuide from 'screens/Auth/Login/Articles/ConditionGuide';
import ShippingGuide from 'screens/Auth/Login/Articles/ShippingGuide';
import HowItWorks from 'screens/Auth/Login/Articles/HowItWorks';
import SustainableEconomics from 'screens/Auth/Login/Articles/SustainableEconomics';
import ArticlesHub from 'screens/Public/Articles/ArticlesHub';
import ComponentPlayground from 'screens/Dev/ComponentPlayground';
import ViewCardPage from 'screens/Main/ViewCard/ViewCardPage';
import ViewCardPrintings from 'screens/Main/ViewCardPrintings/ViewCardPrintings';
import SearchPage from 'screens/Main/Search/SearchPage';
import SearchResultsPage from 'screens/Main/Search/SearchResultsPage';
import SendPage from 'screens/Main/tabs/Send/SendPage2';
import ActiveTradesPage from 'screens/Main/tabs/Send/ActiveTradesPage';
import ActiveReceivingPage from 'screens/Main/tabs/Receive/ActiveReceivingPage';
import InboxPage from 'screens/Main/tabs/Inbox/InboxPage';
import ConversationThreadPage from 'screens/Main/tabs/Inbox/ConversationThreadPage';
import TransactionHistoryPage from 'screens/Main/TransactionHistory/TransactionHistoryPage';
import { Settings } from 'screens/Settings';
import UsersPage from 'screens/Admin/Users/UsersPage';
import UserDetailPage from 'screens/Admin/UserDetail/UserDetailPage';
import CardPricingPage from 'screens/Admin/CardPricing/CardPricingPage';
import FeedbackPage from 'screens/Admin/Feedback/FeedbackPage';
import TradesPage from 'screens/Admin/Trades/TradesPage';
import ControlsPage from 'screens/Admin/Controls/ControlsPage';
import AnalyticsPage from 'screens/Admin/Analytics/AnalyticsPage';
import CasesPage from 'screens/Admin/Cases/CasesPage';
import CaseDetailPage from 'screens/Admin/Cases/CaseDetailPage';
import AdminTransactionsPage from 'screens/Admin/Transactions/AdminTransactionsPage';
import AdminCronJobsPage from 'screens/Admin/CronJobs/AdminCronJobsPage';
import AdminRoute from './AdminRoute';
import BlogRoute from './BlogRoute';
import PasswordResetPage from 'screens/Auth/PasswordReset/PasswordResetPage';
import ArticlesListPage from 'screens/Admin/Blog/ArticlesListPage';
import ArticleEditorPage from 'screens/Admin/Blog/ArticleEditorPage';
import ArticlePreviewPage from 'screens/Admin/Blog/ArticlePreviewPage';
import DynamicArticle from 'screens/Public/DynamicArticle/DynamicArticle';
import FAQ from 'screens/Public/FAQ';
import ElitePage from 'screens/Public/Elite';
import EternalDurdlesPage from 'screens/Public/EternalDurdles/EternalDurdlesPage';
import Compare from 'screens/Public/Compare';
import LeaderboardPage from 'screens/Public/Leaderboard';
import SetsIndexPage from 'screens/Public/Sets/SetsIndexPage';
import SetDetailPage from 'screens/Public/Sets/SetDetailPage';
import CampaignStatsPage from 'screens/Admin/DripCampaign/CampaignStatsPage';
import BroadcastPage from 'screens/Admin/Broadcast/BroadcastPage';
import SeoReportPage from 'screens/Admin/SEO/SeoReportPage';
import EmailVerificationPage from 'screens/Auth/EmailVerification/EmailVerificationPage';
import AccountVerificationPage from 'screens/Auth/AccountVerification/AccountVerificationPage';
import { useGetPublicProfileQuery } from 'store/services/profile/profileApi';
import UserProfilePage from 'screens/Public/UserProfile/UserProfilePage';
import SlugProfilePage from 'screens/Public/SlugProfile/SlugProfilePage';
import NotFound404 from 'screens/NotFound404/NotFound404';
import UniversalImportPage from 'screens/Public/UniversalImport/UniversalImportPage';
import ApiDocumentationPage from 'screens/Public/ApiDocumentation';
import ActivityPage from 'screens/Main/tabs/Activity/ActivityPage';
import FeedPage from 'screens/Main/tabs/Feed/FeedPage';
import NewsfeedPage from 'screens/Main/Newsfeed/NewsfeedPage';
import newsfeedIcon from 'assets/icons/newsfeed-icon.svg';
import { AssetUrls } from 'config/assets';
import MaintenanceGuard from './MaintenanceGuard';
import { SearchFromAnywhere } from 'components/SearchFromAnywhere';
import ScrollToTop from 'components/ScrollToTop';

export interface MainRoutesProps {
  path: string;
  Component: React.ReactNode;
  title?: string;
  icons?: {
    default: string;
    selected: string;
  };
  children?: React.ReactNode;
  backgroundImage?: string;
}

export const MY_CARDS_ROUTES = ['want-list', 'have-list'];

export const MAIN_ROUTES: MainRoutesProps[] = [
  {
    path: '/home',
    Component: <HomePage />,
    title: 'Home',
    icons: {
      default: AssetUrls.icons.navigation.adminHome,
      selected: AssetUrls.icons.navigation.adminHome,
    },
    backgroundImage: 'https://d3dmfym8zvsq5m.cloudfront.net/cards/art_crop/6243f79f-b0e3-4fba-b899-7535e1a277e2.jpg',
  },
  {
    path: '/my-cards',
    Component: <MyCards />,
    title: 'Collection',
    icons: {
      default: AssetUrls.icons.navigation.collection,
      selected: AssetUrls.icons.navigation.collection,
    },
    children: (
      <>
        {MY_CARDS_ROUTES.map((route, index) => (
          <Route key={route} index={index === 0} path={route} element={null} />
        ))}
      </>
    ),
  },
  {
    path: '/sending',
    Component: <ActiveTradesPage />,
    title: 'Sending',
    icons: {
      default: AssetUrls.icons.navigation.sending,
      selected: AssetUrls.icons.navigation.sending,
    },
  },
  {
    path: '/receiving',
    Component: <ActiveReceivingPage />,
    title: 'Receiving',
    icons: {
      default: AssetUrls.icons.navigation.receiving,
      selected: AssetUrls.icons.navigation.receiving,
    },
  },
  {
    path: '/inbox',
    Component: <InboxPage />,
    title: 'Inbox',
    icons: {
      default: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg',
      selected: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg',
    },
    backgroundImage: 'https://d3dmfym8zvsq5m.cloudfront.net/cards/art_crop/f7cf3bbf-acc0-4fe7-a631-aca698190ce2.jpg',
  },
  {
    path: '/leaderboard',
    Component: <LeaderboardPage />,
    title: 'Leaderboard',
    icons: {
      default: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/trophy.svg',
      selected: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/trophy.svg',
    },
  },
  {
    path: '/search',
    Component: <SearchPage />,
    title: 'Search',
    icons: {
      default: AssetUrls.icons.navigation.search,
      selected: AssetUrls.icons.navigation.search,
    },
    backgroundImage: AssetUrls.cards.samples.trygonPredator,
  },
  {
    path: '/articles',
    Component: <ArticlesHub />,
    title: 'Articles',
    icons: {
      default: AssetUrls.icons.navigation.guide,
      selected: AssetUrls.icons.navigation.guide,
    },
  },
  {
    path: '/newsfeed',
    Component: <NewsfeedPage />,
    title: 'Newsfeed',
    icons: {
      default: newsfeedIcon,
      selected: newsfeedIcon,
    },
  },
];

const protectedRoutes = MAIN_ROUTES.filter(
  (route) => route.path !== '/articles' && route.path !== '/search' && route.path !== '/leaderboard' // Articles, Search, and Leaderboard are public, not protected
).map((route) => ({
  path: route.path,
  element: <Main currentTab={route} backgroundImage={route.backgroundImage} />,
  children: route.children,
}));

const authRoutes = [{ path: '/welcome', element: <Login /> }];

// Redirect legacy /collection/ routes to profile pages
const CollectionRedirect: React.FC<{ tab: 'have-list' | 'want-list' }> = ({ tab }) => {
  const { username } = useParams<{ username: string }>();
  const { data: profile, isLoading } = useGetPublicProfileQuery(username ?? '', { skip: !username });

  if (!username) return <Navigate replace to="/" />;
  if (isLoading) return null;

  const basePath = profile?.customSlug
    ? `/u/${profile.customSlug}`
    : `/profile/u/${username}`;

  return <Navigate replace to={`${basePath}?tab=${tab}`} />;
};

// Analytics tracking component (must be inside BrowserRouter)
const AnalyticsTracker = () => {
  const location = useLocation();

  // Capture referral source on landing for marketing attribution
  // Supports both ?ref=partner and ?utm_source=partner (ref takes priority)
  // Only runs once on mount to capture initial landing URL params
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referralSource = params.get('ref') || params.get('utm_source');
    if (referralSource) {
      try {
        localStorage.setItem('referral_source', referralSource);
      } catch {
        // localStorage not available
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only capture referral from initial landing URL

  React.useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

const Router = () => {
  console.log('🔷 Router: Component rendering', { pathname: window.location.pathname });

  const isAuthenticated = useSelector((state: RootState) => state.auth.token);

  console.log('🔷 Router: Auth check', { isAuthenticated: !!isAuthenticated });

  const defaultPathNavigate = (
    <Navigate replace to={isAuthenticated ? '/home' : '/welcome'} />
  );

  console.log('🔷 Router: About to return BrowserRouter');

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsTracker />
      <SearchFromAnywhere />
      <MaintenanceGuard>
        <Routes>
          <Route path="/dev" element={<ComponentPlayground />} />

        {/* Legacy collection routes → redirect to profile */}
        <Route
          path="/collection/have-list/:username"
          element={<CollectionRedirect tab="have-list" />}
        />
        <Route
          path="/collection/want-list/:username"
          element={<CollectionRedirect tab="want-list" />}
        />

        {/* Public User Profile Routes */}
        <Route path="/profile/u/:username" element={<UserProfilePage />} />
        <Route path="/u/:slug" element={<SlugProfilePage />} />

        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/onboarding" element={<OnboardingFlow />} />
        <Route path="/onboarding/start" element={<OnboardingFlow />} />

        {/* Articles Hub - Public route with auth-aware sidebar */}
        <Route path="/articles" element={<ArticlesHub />} />
        {/* FAQ - Public route with auth-aware sidebar */}
        <Route path="/faq" element={<FAQ />} />
        {/* Elite - Public route with auth-aware sidebar */}
        <Route path="/elite" element={<ElitePage />} />
        {/* Eternal Durdles partner landing - Public route */}
        <Route path="/eternaldurdles" element={<EternalDurdlesPage />} />
        {/* Leaderboard - Public route with auth-aware sidebar */}
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        {/* Compare Page - Public route for SEO */}
        <Route path="/compare" element={<Compare />} />
        {/* API Documentation - Public route for developers */}
        <Route path="/docs/api" element={<ApiDocumentationPage />} />
        {/* Universal Import - Public routes for partner integrations */}
        <Route path="/import/:partner" element={<UniversalImportPage />} />
        <Route path="/import" element={<UniversalImportPage />} />
        {/* Sets Directory - Public routes for SEO */}
        <Route path="/sets" element={<SetsIndexPage />} />
        <Route path="/sets/:setCode" element={<SetDetailPage />} />
        <Route path="/articles/how-it-works" element={<HowItWorks />} />
        <Route path="/articles/sustainable-economics" element={<SustainableEconomics />} />
        <Route path="/articles/condition-guide" element={<ConditionGuide />} />
        <Route path="/articles/shipping" element={<ShippingGuide />} />
        {/* Dynamic CMS Articles - Must be last to avoid conflicting with hardcoded routes */}
        <Route path="/articles/:slug" element={<DynamicArticle />} />
        <Route path="/reset-password/:token" element={<PasswordResetPage />} />
        <Route path="/verify-email-change/:token" element={<EmailVerificationPage />} />
        <Route path="/verify-email/:token" element={<AccountVerificationPage />} />
        {/* Search Page - Public route for SEO */}
        <Route
          path="/search"
          element={
            <Main
              currentTab={{
                path: '/search',
                Component: <SearchPage />,
                title: 'Search',
                icons: {
                  default: AssetUrls.icons.navigation.search,
                  selected: AssetUrls.icons.navigation.search,
                },
              }}
              backgroundImage={AssetUrls.cards.samples.trygonPredator}
            />
          }
        />
        {/* Search Results - Public route for SEO */}
        <Route
          path="/search/results"
          element={
            <Main
              currentTab={{
                path: '/search/results',
                Component: <SearchResultsPage />,
                title: 'Search Results',
              }}
              backgroundImage={AssetUrls.cards.samples.trygonPredator}
            />
          }
        />

        {/* Transaction History */}
        <Route
          path="/transaction-history"
          element={
            <ProtectedRoute>
              <Main
                currentTab={{
                  path: '/transaction-history',
                  Component: <TransactionHistoryPage />,
                  title: 'Transaction History',
                }}
              />
            </ProtectedRoute>
          }
        />

        {/* Conversation Thread */}
        <Route
          path="/inbox/:conversationId"
          element={
            <ProtectedRoute>
              <Main
                currentTab={{
                  path: '/inbox',
                  Component: <ConversationThreadPage />,
                  title: 'Messages',
                }}
              />
            </ProtectedRoute>
          }
        />

        {/* View Card Printings */}
        <Route
          path="/view-card-printings/:cardName"
          element={
            <ViewCardPrintings
              currentTab={{
                path: '/my-cards',
                Component: <></>,
              }}
            />
          }
        />

        {/* View Card Page — new slug URL format */}
        <Route
          path="/cards/:cardSlug/:setCode/:collectorNumber"
          element={
            <ViewCardPage
              currentTab={{
                path: '/my-cards',
                Component: <></>,
              }}
            />
          }
        />

        {/* View Card Page — legacy UUID URL (redirects to slug URL) */}
        <Route
          path="/cards/:cardId"
          element={
            <ViewCardPage
              currentTab={{
                path: '/my-cards',
                Component: <></>,
              }}
            />
          }
        />

        {/* Send Cards Page (Not in MAIN_ROUTES) */}
        <Route
          path="/sendcards"
          element={
            <ProtectedRoute>
              <Main
                currentTab={{
                  path: '/sendcards',
                  Component: <SendPage />,
                  title: 'Send Cards',
                  icons: {
                    default: `${AssetUrls.base}/assets/icons/navigation/sending-icon.svg`,
                    selected: `${AssetUrls.base}/assets/icons/navigation/sending-icon.svg`,
                  },
                  backgroundImage: AssetUrls.cards.samples.trygonPredator,
                }}
                backgroundImage={AssetUrls.cards.samples.trygonPredator}
              />
            </ProtectedRoute>
          }
        />


        {/* Settings Page */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Main
                currentTab={{
                  path: '/settings',
                  Component: <Settings />,
                  title: 'Settings',
                }}
                backgroundImage="https://d3dmfym8zvsq5m.cloudfront.net/cards/art_crop/0c4eaecf-dd4c-45ab-9b50-2abe987d35d4.jpg"
              />
            </ProtectedRoute>
          }
        />

        {/* Activity Feed Page */}
        <Route
          path="/home/activity"
          element={
            <ProtectedRoute>
              <Main
                currentTab={{
                  path: '/home/activity',
                  Component: <ActivityPage />,
                  title: 'Activity',
                }}
              />
            </ProtectedRoute>
          }
        />

        {/* Community Feed (intentionally not in MAIN_ROUTES / sidebar for v1) */}
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <Main
                currentTab={{
                  path: '/feed',
                  Component: <FeedPage />,
                  title: 'Community Feed',
                }}
              />
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route path="/admin/dashboard" element={<AdminRoute><AnalyticsPage /></AdminRoute>} />
        <Route path="/admin/controls" element={<AdminRoute><ControlsPage /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="/admin/users/:userId" element={<AdminRoute><UserDetailPage /></AdminRoute>} />
        <Route path="/admin/cards" element={<AdminRoute><CardPricingPage /></AdminRoute>} />
        <Route path="/admin/cases" element={<AdminRoute><CasesPage /></AdminRoute>} />
        <Route path="/admin/cases/:caseId" element={<AdminRoute><CaseDetailPage /></AdminRoute>} />
        <Route path="/admin/trades" element={<AdminRoute><TradesPage /></AdminRoute>} />
        <Route path="/admin/transactions" element={<AdminRoute><AdminTransactionsPage /></AdminRoute>} />
        <Route path="/admin/cronjobs" element={<AdminRoute><AdminCronJobsPage /></AdminRoute>} />
        <Route path="/admin/drip-campaign" element={<AdminRoute><CampaignStatsPage /></AdminRoute>} />
        <Route path="/admin/message" element={<AdminRoute><BroadcastPage /></AdminRoute>} />
        <Route path="/admin/feedback" element={<AdminRoute><FeedbackPage /></AdminRoute>} />
        <Route path="/admin/seo" element={<AdminRoute><SeoReportPage /></AdminRoute>} />
        {/* Admin Blog Routes - accessible by admins and writers */}
        <Route path="/admin/blog/articles" element={<BlogRoute><ArticlesListPage /></BlogRoute>} />
        <Route path="/admin/blog/articles/new" element={<BlogRoute><ArticleEditorPage /></BlogRoute>} />
        <Route path="/admin/blog/articles/:id/edit" element={<BlogRoute><ArticleEditorPage /></BlogRoute>} />
        <Route path="/admin/blog/articles/:id/preview" element={<BlogRoute><ArticlePreviewPage /></BlogRoute>} />

        {/* Login redirect to welcome */}
        <Route path="/login" element={<Navigate replace to="/welcome" />} />
        
        {/* Default Redirect */}
        <Route path="" element={defaultPathNavigate} />

        {/* Protected Routes */}
        {protectedRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<ProtectedRoute>{route.element}</ProtectedRoute>}
          >
            {route.children}
          </Route>
        ))}

        {/* Auth Routes */}
        {authRoutes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={<AuthRoute>{route.element}</AuthRoute>}
          />
        ))}

        {/* 404 Not Found - Catch all unmatched routes */}
        <Route path="*" element={<NotFound404 />} />
      </Routes>
      </MaintenanceGuard>
    </BrowserRouter>
  );
};

export default Router;
